import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { Link } from "wouter";
import { ArrowLeft, TrendingUp, CheckCircle } from "lucide-react";

const BANDS = [
  {
    band: "Band 6",
    title: "Clinical Pharmacist (Rotational / Specialist)",
    salary: "£37,338 – £44,962",
    desc: "Entry-level clinical pharmacist, typically completing foundation year (FY1) or on rotation. Developing clinical skills, working under supervision of senior pharmacists.",
    requirements: [
      "MPharm + pre-registration / foundation year completion",
      "GPhC registration",
      "Working towards or completing first post-registration qualification (e.g. PGCert)",
    ],
  },
  {
    band: "Band 7",
    title: "Senior Clinical Pharmacist / Specialist Pharmacist",
    salary: "£46,148 – £52,809",
    desc: "Specialist pharmacist working in a defined area such as rheumatology. Works with increasing autonomy, may have NMP qualification. Contributes to service development.",
    requirements: [
      "Demonstrable specialist knowledge in area of practice",
      "Postgraduate qualification (PGCert/PGDip/MSc preferred)",
      "Evidence of clinical leadership and service development contribution",
    ],
  },
  {
    band: "Band 8a",
    title: "Lead Clinical Pharmacist / Advanced Pharmacist Practitioner",
    salary: "£53,755 – £60,504",
    desc: "Leads a defined service area. May hold independent prescribing qualification. Supervises Band 6/7 pharmacists. Involved in trust formulary and governance.",
    requirements: [
      "Advanced clinical knowledge in specialist area",
      "Independent prescribing qualification (preferred)",
      "Leadership and management experience",
      "Working towards or achieved RPS Advanced Credentialing",
    ],
  },
  {
    band: "Band 8b",
    title: "Principal Pharmacist / Consultant Pharmacist (Early)",
    salary: "£62,215 – £72,293",
    desc: "Manages a significant pharmacy service or team. Strategic planning for medicines optimisation at department or divisional level. May hold Consultant Pharmacist designation.",
    requirements: [
      "Extensive specialist expertise",
      "RPS Advanced or Consultant Credentialing",
      "Track record of service improvement and innovation",
      "Significant management/leadership experience",
    ],
  },
  {
    band: "Band 8c",
    title: "Consultant Pharmacist",
    salary: "£74,290 – £85,601",
    desc: "Senior leadership role with four pillars: Expert Clinical Practice, Leadership & Management, Education & Training, Research. Usually holds Consultant Pharmacist credential from RPS Faculty.",
    requirements: [
      "RPS Consultant Pharmacist credentialing (or actively pursuing)",
      "PhD or equivalent advanced academic qualification (common)",
      "National profile in specialist area",
      "Demonstrated innovation and research contribution",
    ],
  },
  {
    band: "Band 8d",
    title: "Consultant Pharmacist / Deputy Chief Pharmacist",
    salary: "£88,168 – £101,677",
    desc: "Most senior clinical or operational pharmacist below Chief Pharmacist. May hold an organisational or sub-regional leadership role. Significant strategic and governance responsibilities.",
    requirements: [
      "Established national/international reputation",
      "Broad strategic leadership beyond specialist area",
      "Significant research and academic portfolio",
    ],
  },
];

export default function NhsBanding() {
  return (
    <PharmacistLayout title="NHS Banding & Progression">
      <div className="space-y-6 max-w-4xl">
        <Link href="/pharmacist/career">
          <button className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-2">
            <ArrowLeft className="h-4 w-4" />
            Career Pathways
          </button>
        </Link>

        <div className="bg-gradient-to-br from-violet-900 to-violet-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="h-7 w-7 text-violet-300" />
            <div>
              <h2 className="text-xl font-bold">NHS Band Progression</h2>
              <p className="text-violet-300 text-sm">Agenda for Change · Pharmacy Career Ladder</p>
            </div>
          </div>
          <p className="text-violet-200 text-sm leading-relaxed">
            Pharmacy careers in the NHS are graded under Agenda for Change (AfC). Salaries shown are 2024/25 national AfC rates for England (excluding London weighting). Your annual increment progresses within the band; promotion requires a change of post.
          </p>
        </div>

        <div className="space-y-4">
          {BANDS.map((b) => (
            <div key={b.band} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="shrink-0 bg-violet-100 text-violet-800 font-bold text-sm rounded-xl px-3 py-1.5 mt-0.5">
                  {b.band}
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-bold text-slate-900 leading-snug">{b.title}</h3>
                      <p className="text-xs font-medium text-violet-700 mt-0.5">{b.salary}</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mt-2">{b.desc}</p>
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">Typical requirements</p>
                    <ul className="space-y-1">
                      {b.requirements.map((r, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                          <CheckCircle className="h-3.5 w-3.5 text-violet-500 shrink-0 mt-0.5" />
                          {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-violet-50 border border-violet-200 rounded-2xl p-5">
          <h4 className="font-bold text-violet-900 mb-2">Job Planning (PAs)</h4>
          <p className="text-sm text-slate-700 leading-relaxed">
            Senior pharmacists (Band 8a and above) typically negotiate a job plan with their manager. A standard whole-time equivalent is <strong>10 Programmed Activities (PAs)</strong> per week. For advanced practitioners, a minimum of <strong>2.5 SPAs (Supporting Professional Activities)</strong> is recommended to allow time for CPD, research, teaching, and service improvement.
          </p>
        </div>

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          Salary figures are 2024/25 NHS Agenda for Change national pay scales for England. London High Cost Area Supplement adds 20% inner / 15% outer to base salary. Actual banding is determined by job evaluation under NHS Agenda for Change and may vary by organisation.
        </div>
      </div>
    </PharmacistLayout>
  );
}
